import axios from 'axios';
import { tenantSlugFromHostname } from '../validators.js';
import { notifyNetworkError, notifyError } from '../toast.js';
import { getDevPortalAccessToken } from '../devPortalSession.js';
import { adminMfaCodeFrom, requestElevation, isAdminMfaDialogMounted } from './adminMfaGate.js';

export const API_URL = import.meta.env.VITE_API_URL || '/api';

// Origin for server-returned paths that already carry the /api prefix (e.g.
// the /api/alumni-photos/:id proxy paths from the photo bulk endpoint).
// API_URL ends in /api both locally ('/api') and in prod
// ('https://api.coffeechats.me/api'), so concatenating API_URL onto such a
// path doubles the prefix into /api/api/... — same definition as the student
// client's API_BASE_URL (client/services/api.js).
export const API_BASE_URL = API_URL.replace(/\/api\/?$/, '');

// Build-time realm: 'admin' for the console bundle, 'portal' for the portal
// bundle. Tells the API which named session cookie this surface owns, so a
// student signing in on coffeechats.me can't clobber the console's session.
// See api/src/modules/auth/services/authRealm.js. Build-time (not a hostname
// sniff) because console and portal are separate Vite builds.
const AUTH_REALM = import.meta.env.VITE_AUTH_REALM || '';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Tenant interceptor: this is how the PORTAL tells the server which school it is.
// `tenantSlugFromHostname` reads window.location.hostname's first label and
// returns a validated slug (^[a-z0-9-]+$, len 1-63) — or null when there is no
// tenant. On admin.coffeechats.me the first label 'admin' is a RESERVED_SLUG, so
// the validator returns null and we omit the header entirely (the super-admin
// console is not institution-scoped). When a slug is present we stamp it on
// X-Institution-Slug; super-admin drill-in still overrides per-call via the
// explicit ?institutionId= query param the service wrappers add.
api.interceptors.request.use((config) => {
  const slug = tenantSlugFromHostname();
  if (slug) {
    config.headers['X-Institution-Slug'] = slug;
  }
  if (AUTH_REALM) {
    config.headers['X-Auth-Realm'] = AUTH_REALM;
  }
  if (import.meta.env.DEV && AUTH_REALM === 'portal') {
    const devAccessToken = getDevPortalAccessToken();
    if (devAccessToken) {
      config.headers.Authorization = `Bearer ${devAccessToken}`;
    }
  }
  return config;
});

// Response interceptor, two branches:
//
// 1. Network drop / timeout (no response from the server): show ONE "Lost
//    network connection" toast and latch it for the outage window.
// 2. Admin step-up MFA: a 403 carrying ADMIN_MFA_REQUIRED or
//    ADMIN_MFA_ENROLLMENT_REQUIRED means this session is not elevated. Ask the
//    mounted dialog for an elevation and, once granted, replay the request so
//    the caller never learns it was interrupted. The `_mfaRetry` flag bounds it
//    to one replay. Both the console and institution portal mount the shared
//    dialog; the no-dialog fallback remains for tests or a future bundle that
//    reuses this service without mounting the UI.
//
// Every other error passes through untouched for the caller to handle.
api.interceptors.response.use(
  (response) => { return response; },
  async (error) => {
    if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
      notifyNetworkError();
      return Promise.reject(error);
    }

    const mfaCode = adminMfaCodeFrom(error);
    if (mfaCode && error.config && !error.config._mfaRetry) {
      error.config._mfaRetry = true;
      if (await requestElevation(mfaCode)) {
        return api(error.config);
      }
      // A bundle that forgets to mount the dialog must fail closed but still
      // explain the dead end. Deliberately NOT shown when a dialog exists and
      // the admin cancelled it: that was a choice, not a missing capability.
      if (!isAdminMfaDialogMounted()) {
        notifyError('Admin actions need two-factor authentication.');
      }
    }

    return Promise.reject(error);
  },
);

export { api };
export default api;
